import React from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { Logo } from '../../components/Logo';
import { AuthMode, LoginViewModel } from './viewModel';

interface SignInForm {
  email: string;
  password: string;
}

interface SignUpForm extends SignInForm {
  displayName: string;
  confirmPassword: string;
}

interface LoginViewProps {
  vm: LoginViewModel;
}

export function DesktopLoginView({ vm }: LoginViewProps) {
  const signInForm = useForm<SignInForm>();
  const signUpForm = useForm<SignUpForm>();

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        <div className="hidden lg:flex lg:w-1/2 bg-primary text-white flex-col justify-center items-center p-12">
          <ClipboardCheck className="h-20 w-20 mb-6" />
          <h1 className="text-4xl font-bold mb-4"><Logo /></h1>
          <p className="text-xl text-white/80 text-center max-w-md">
            A focused queue for expert annotation and peer review.
          </p>
        </div>

        <div className="flex-1 flex flex-col justify-center items-center p-8 bg-gray-50">
          <div className="w-full max-w-md">
            <DesktopBrand />
            <DesktopTabs mode={vm.mode} onChange={vm.setMode} />
            <ErrorMessage error={vm.error} />
            <AuthForms
              vm={vm}
              signInForm={signInForm}
              signUpForm={signUpForm}
              inputClassName="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              buttonClassName="w-full py-2 px-4 bg-primary text-white font-medium rounded-lg hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            />
            <Divider backgroundClassName="bg-gray-50" />
            <GoogleButton vm={vm} />
          </div>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}

export function MobileLoginView({ vm }: LoginViewProps) {
  const signInForm = useForm<SignInForm>();
  const signUpForm = useForm<SignUpForm>();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-5 pt-8 pb-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary/10 text-primary">
            <ClipboardCheck className="h-6 w-6" />
          </span>
          <div className="min-w-0">
            <Logo className="text-2xl font-bold text-gray-900" />
            <p className="text-sm text-gray-500 mt-0.5">Annotation and review queue</p>
          </div>
        </div>
      </header>

      <main className="flex-1 px-5 py-6">
        <MobileModeSwitch mode={vm.mode} onChange={vm.setMode} />
        <ErrorMessage error={vm.error} />
        <AuthForms
          vm={vm}
          signInForm={signInForm}
          signUpForm={signUpForm}
          inputClassName="w-full min-h-[48px] px-4 py-3 border border-gray-300 rounded-md text-base focus:ring-2 focus:ring-primary focus:border-transparent"
          buttonClassName="w-full min-h-[48px] py-3 px-4 bg-primary text-white font-medium rounded-md hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        />
        <Divider backgroundClassName="bg-white" />
        <GoogleButton vm={vm} mobile />
      </main>

      <LegalFooter compact />
    </div>
  );
}

function DesktopBrand() {
  return (
    <div className="lg:hidden flex items-center justify-center mb-8">
      <ClipboardCheck className="h-10 w-10 text-primary mr-2" />
      <Logo className="text-2xl font-bold text-gray-900" />
    </div>
  );
}

interface ModeSwitchProps {
  mode: AuthMode;
  onChange: (mode: AuthMode) => void;
}

function DesktopTabs({ mode, onChange }: ModeSwitchProps) {
  return (
    <div className="flex border-b border-gray-200 mb-6">
      <AuthTab label="Sign In" mode="signin" activeMode={mode} onChange={onChange} />
      <AuthTab label="Sign Up" mode="signup" activeMode={mode} onChange={onChange} />
    </div>
  );
}

function AuthTab({ label, mode, activeMode, onChange }: { label: string; mode: AuthMode; activeMode: AuthMode; onChange: (mode: AuthMode) => void }) {
  const active = mode === activeMode;
  return (
    <button
      type="button"
      onClick={() => onChange(mode)}
      className={`flex-1 py-3 text-center font-medium transition-colors ${
        active ? 'text-primary border-b-2 border-primary' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
    </button>
  );
}

function MobileModeSwitch({ mode, onChange }: ModeSwitchProps) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-md bg-gray-100 p-1 mb-5" role="tablist">
      {[
        { id: 'signin' as const, label: 'Sign In' },
        { id: 'signup' as const, label: 'Sign Up' },
      ].map((item) => {
        const active = item.id === mode;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={`min-h-[44px] rounded px-3 text-sm font-medium transition-colors ${
              active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

interface AuthFormsProps {
  vm: LoginViewModel;
  signInForm: UseFormReturn<SignInForm>;
  signUpForm: UseFormReturn<SignUpForm>;
  inputClassName: string;
  buttonClassName: string;
}

function AuthForms({ vm, signInForm, signUpForm, inputClassName, buttonClassName }: AuthFormsProps) {
  if (vm.mode === 'signin') {
    return (
      <form onSubmit={signInForm.handleSubmit((data) => vm.signIn(data.email, data.password))} className="space-y-4">
        <Field
          label="Email"
          htmlFor="signin-email"
          error={signInForm.formState.errors.email?.message}
        >
          <input
            id="signin-email"
            type="email"
            autoComplete="email"
            aria-invalid={Boolean(signInForm.formState.errors.email)}
            aria-describedby={signInForm.formState.errors.email ? 'signin-email-error' : undefined}
            {...signInForm.register('email', {
              required: 'Enter your email address.',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
            })}
            className={inputClassName}
            placeholder="you@example.com"
          />
        </Field>
        <Field
          label="Password"
          htmlFor="signin-password"
          error={signInForm.formState.errors.password?.message}
        >
          <input
            id="signin-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={Boolean(signInForm.formState.errors.password)}
            aria-describedby={signInForm.formState.errors.password ? 'signin-password-error' : undefined}
            {...signInForm.register('password', { required: 'Enter your password.' })}
            className={inputClassName}
            placeholder="Enter your password"
          />
        </Field>
        <div className="text-right text-sm">
          <button
            type="button"
            disabled={vm.busy}
            onClick={async () => {
              if (await signInForm.trigger('email')) {
                await vm.resetPassword(signInForm.getValues('email'));
              }
            }}
            className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Forgot your Workbench password?
          </button>
        </div>
        {vm.resetMessage ? (
          <p role="status" className="text-sm text-green-800">{vm.resetMessage}</p>
        ) : null}
        <SubmitButton busy={vm.busy} className={buttonClassName}>Sign In</SubmitButton>
      </form>
    );
  }

  return (
    <form
      onSubmit={signUpForm.handleSubmit((data) =>
        vm.signUp(data.email, data.password, data.displayName, data.confirmPassword),
      )}
      className="space-y-4"
    >
      <Field
        label="Display Name"
        htmlFor="signup-name"
        error={signUpForm.formState.errors.displayName?.message}
      >
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          aria-invalid={Boolean(signUpForm.formState.errors.displayName)}
          aria-describedby={signUpForm.formState.errors.displayName ? 'signup-name-error' : undefined}
          {...signUpForm.register('displayName', { required: 'Enter your name.' })}
          className={inputClassName}
          placeholder="Your name"
        />
      </Field>
      <Field
        label="Email"
        htmlFor="signup-email"
        error={signUpForm.formState.errors.email?.message}
      >
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          aria-invalid={Boolean(signUpForm.formState.errors.email)}
          aria-describedby={signUpForm.formState.errors.email ? 'signup-email-error' : undefined}
          {...signUpForm.register('email', {
            required: 'Enter your email address.',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
          })}
          className={inputClassName}
          placeholder="you@example.com"
        />
      </Field>
      <Field
        label="Password"
        htmlFor="signup-password"
        error={signUpForm.formState.errors.password?.message}
      >
        <input
          id="signup-password"
          type="password"
          autoComplete="new-password"
          aria-invalid={Boolean(signUpForm.formState.errors.password)}
          aria-describedby={signUpForm.formState.errors.password ? 'signup-password-error' : undefined}
          {...signUpForm.register('password', {
            required: 'Enter a password.',
            minLength: { value: 6, message: 'Use a password with at least 6 characters.' },
          })}
          className={inputClassName}
          placeholder="At least 6 characters"
        />
      </Field>
      <Field
        label="Confirm Password"
        htmlFor="signup-confirm-password"
        error={signUpForm.formState.errors.confirmPassword?.message}
      >
        <input
          id="signup-confirm-password"
          type="password"
          autoComplete="new-password"
          aria-invalid={Boolean(signUpForm.formState.errors.confirmPassword)}
          aria-describedby={signUpForm.formState.errors.confirmPassword ? 'signup-confirm-password-error' : undefined}
          {...signUpForm.register('confirmPassword', {
            required: 'Confirm your password.',
            validate: (value) => value === signUpForm.getValues('password') || 'The passwords do not match.',
          })}
          className={inputClassName}
          placeholder="Confirm your password"
        />
      </Field>
      <SubmitButton busy={vm.busy} className={buttonClassName}>Sign Up</SubmitButton>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="mt-1 text-sm text-red-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function SubmitButton({ busy, className, children }: { busy: boolean; className: string; children: React.ReactNode }) {
  return (
    <button type="submit" disabled={busy} className={className}>
      {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : children}
    </button>
  );
}

function ErrorMessage({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      aria-live="polite"
      className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm"
    >
      {error}
    </div>
  );
}

function Divider({ backgroundClassName }: { backgroundClassName: string }) {
  return (
    <div className="relative my-6">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-300" />
      </div>
      <div className="relative flex justify-center text-sm">
        <span className={`px-2 ${backgroundClassName} text-gray-500`}>Or continue with</span>
      </div>
    </div>
  );
}

function GoogleButton({ vm, mobile = false }: { vm: LoginViewModel; mobile?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => vm.signInWithGoogle()}
      disabled={vm.busy}
      className={`w-full border border-gray-300 bg-white text-gray-700 font-medium rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
        mobile ? 'min-h-[48px] py-3 px-4' : 'py-2 px-4'
      }`}
    >
      <GoogleIcon />
      Google
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

function LegalFooter({ compact = false }: { compact?: boolean }) {
  return (
    <footer className={`${compact ? 'px-5 py-4' : 'py-4'} text-center text-xs text-gray-500 bg-gray-50 border-t border-gray-200`}>
      <a href="https://app.alignedhq.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
        Privacy Policy
      </a>
      <span className="mx-2" aria-hidden="true">/</span>
      <a href="https://app.alignedhq.ai/terms" target="_blank" rel="noopener noreferrer" className="hover:text-gray-700">
        Terms of Service
      </a>
      <span className="mx-2" aria-hidden="true">/</span>
      <span>&copy; {new Date().getFullYear()} Aligned</span>
    </footer>
  );
}
